import {
  appApiError,
  appNorseApiRequest,
  appNorseApiRequestCompleted,
} from '@actions/app.action';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { ApiResponseErrorBypassCodes, TOKEN_TIMEOUT_ERROR_CODE, TOKEN_UNAUTHORIZED_ERROR_CODE } from '@constants/app.constants';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { ApiResponse, ProblemApiResponse } from '@models/responses/api-response.model';
import { Store } from '@ngrx/store';
import { selectIsIos } from '@selectors/app.selector';
import { TokenService } from '@services/api-clients';
import { AppInsightsService } from '@services/app-insights/app-insights.service';
import { Observable, Subject } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { EnvironmentService } from '@services/environment-service/environment.service';
import { AuthService } from '@services/auth/auth.service';
import { BBoxService } from '@services/bbox/bbox.service';
import { ApiError } from '@models/app.models';
import { getPathAsArray } from '@utils/data-utils';
import { RudderStackService } from '@services/rudderstack/rudderstack.service';

@Injectable()
export class ApiRequestInterceptor implements HttpInterceptor, OnDestroy {
  isIos$ = this.store.select(selectIsIos);
  onDestroy$ = new Subject<void>();

  isIos = false;

  constructor(
    private store: Store,
    private appInsightsService: AppInsightsService,
    private authService: AuthService,
    private bboxService: BBoxService,
    private rudderStackService: RudderStackService,
  ) {
    this.onInit();
  }

  onInit(): void {
    this.isIos$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((isIos) => (this.isIos = isIos));
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
  }

  handleApiRequestStarted(req: HttpRequest<unknown>): void {
    // this queues the dispatch to run as soon as possible,
    // meaning that if there is ongoing change detection, that
    // will complete first, and thus the value cannot change twice
    // in one change detection cycle, which could otherwise lead to
    // rendering bugs
    setTimeout(() => {
      this.store.dispatch(appNorseApiRequest({ req }));
    }, 1);
  }

  handleApiRequestCompleted(req: HttpRequest<unknown>): void {
    setTimeout(() => {
      this.store.dispatch(appNorseApiRequestCompleted({ req }));
    }, 1);
  }

  handleApiRequestFailed(err: HttpErrorResponse): void {
    if (err instanceof HttpErrorResponse) {
      const error = <ApiResponse<null>>err.error;
      if (err.url?.includes(TokenService.CONTROLLER_ROOT) && err.status === 401) {
        this.bboxService.trackApiError({
          code: TOKEN_UNAUTHORIZED_ERROR_CODE,
        });
      }
      else if (err.status === 440) {
        this.bboxService.trackApiError({
          code: TOKEN_TIMEOUT_ERROR_CODE,
        });
      }
      else if (error.errors) {
        error.errors.forEach((error) => {
          this.bboxService.trackApiError(error);
        });
      }
    }

    if (this.isProblemApiResponse(err.error)) {
      const errors: ApiError[] = [{code: err.error.title}]
      this.store.dispatch(appApiError({ errors }));
      this.appInsightsService.logException(
        {
          name: err.error.type ?? '',
          message: err.error.title ?? ''
        },
        SeverityLevel.Information,
      );
      return;
    }

    if (this.isApiResponse(err.error)) {
      // only handle api errors that are not 404 not found and not expired token
      // errors on token GET, as that error is expected on app start.

      if (err.url) {
        const pathArray = getPathAsArray(err.url);
        const isSkip = pathArray.includes('doccheck');
        if (isSkip) {
          return;
        }
      }

      if (
        err.status !== 404 &&
        !(err.url?.includes(TokenService.CONTROLLER_ROOT) && err.status === 440)
      ) {
        if (
          err.error?.errors &&
          !err.error.errors.some(
            (e) =>
              e.code &&
              (Object.values(ApiResponseErrorBypassCodes) as string[]).includes(
                e.code,
              ),
          )
        ) {
          this.store.dispatch(
            appApiError({
              errors: err.error.errors,
            }),
          );
          err.error.errors.forEach((error) => {
            this.appInsightsService.logException(
              {
                name: error.code ?? '',
                message: error.message ?? '',
                stack: error.details,
              },
              SeverityLevel.Information,
            );
          });
        }
      }
      return;
    }
    if (!(err.url?.includes(TokenService.CONTROLLER_ROOT) && err.status === 401)) {
      this.store.dispatch(appApiError({errors: [{code: err.statusText}]}));
      this.appInsightsService.logException(
        {
          name: err.name ?? '',
          message: err.message ?? ''
        },
        SeverityLevel.Information,
      );
    }
  }

  isApiResponse = (obj: unknown): obj is ApiResponse<null> => {
    return typeof obj === 'object' && obj !== null && Array.isArray((obj as ApiResponse<null>).errors);
  }

  isProblemApiResponse = (obj: unknown): obj is ProblemApiResponse => {
    return typeof obj === 'object' && obj !== null && typeof (obj as ProblemApiResponse).requestId === 'string';
  }

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (!req.url.startsWith(EnvironmentService.environment.apiBaseUrl) && !req.url.startsWith(EnvironmentService.environment.apiPaymentsUrl)) {
        return next.handle(req);
      }

    req = req.clone({
      withCredentials: true,
      headers: this.isIos
        ? req.headers.set('Origin', window.location.origin)
        : req.headers,
    });

    const token = this.authService.getToken();
    if (token) {
      req = req.clone({
        withCredentials: true,
        headers: req.headers,
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
    }

    try {
      const anonymousId = this.rudderStackService.getAnonymousId();
      if (anonymousId) {
        req = req.clone({
          setHeaders: {
            'X-Anonymous-Id': anonymousId,
          },
        });
      }
    } catch {
      console.debug('Failed to get anonymous id');
    }

    const apiRequestObservable$ = next.handle(req) as Observable<
      HttpEvent<unknown>
    >;

    return apiRequestObservable$.pipe(
      tap((resp) => {
        if (resp.type === HttpEventType.Sent) {
          this.handleApiRequestStarted(req);
        }
      }),
      finalize(() => {
        this.handleApiRequestCompleted(req);
      }),
      catchError((err: HttpErrorResponse) => {
        this.handleApiRequestFailed(err);
        throw err;
      }),
    );
  }
}
